import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { fr as frLocale, enUS } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronLeft } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { API } from "../lib/utils";
import { clientApi } from "../lib/clientApi";
import { useClientAuth } from "../context/ClientAuthContext";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const TIME_SLOTS = ["07:00", "08:30", "10:00", "12:00", "14:00", "16:00", "17:30", "19:00"];

const SectionTitle = ({ children }) => (
  <p className="font-heading uppercase tracking-widest text-xs font-bold text-burnt mb-5">{children}</p>
);

const FieldLabel = ({ children, required }) => (
  <label className="block text-sm font-semibold text-forest mb-2">
    {children} {required && <span className="text-burnt">*</span>}
  </label>
);

const BookingPage = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { user } = useClientAuth();
  const [params] = useSearchParams();
  const preselectedPackage = params.get("package") || "";
  const preselectedProgram = params.get("program") || "";
  const preselectedSport = params.get("sport") || "";

  const [packages, setPackages] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({
    package_id: preselectedPackage,
    full_name: "",
    gender: "",
    age: "",
    region: "",
    sport_interest: preselectedSport,
    level: "",
    goals: "",
    email: "",
    phone: "",
    preferred_date: null,
    preferred_time: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const notesPrefilled = useRef(false);
  const contactPrefilled = useRef(false);

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPackages(r.data));
    axios.get(`${API}/programs`).then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && !contactPrefilled.current) {
      contactPrefilled.current = true;
      setForm((f) => ({
        ...f,
        full_name: f.full_name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
        region: f.region || (user.city && /montr/i.test(user.city) ? "montreal" : f.region),
      }));
    }
  }, [user]);

  const programObj = programs.find((p) => p.slug === preselectedProgram);
  const programTitle = programObj
    ? (lang === "fr" ? programObj.title_fr : programObj.title_en)
    : (preselectedProgram ? t(`kidspage.${preselectedProgram}_title`) : "");

  useEffect(() => {
    if (preselectedProgram && programTitle && !notesPrefilled.current) {
      notesPrefilled.current = true;
      setForm((f) =>
        f.notes
          ? f
          : { ...f, notes: lang === "fr" ? `Programme souhaité : ${programTitle}` : `Requested program: ${programTitle}` }
      );
    }
  }, [programTitle, preselectedProgram, lang]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setVal = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const selectedPkg = packages.find((p) => p.id === form.package_id);

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["package_id", "full_name", "email", "phone", "level", "preferred_date"];
    for (const k of required) {
      if (!form[k]) {
        return toast.error(
          lang === "fr"
            ? "Veuillez remplir tous les champs requis (*)."
            : "Please fill all required fields (*)."
        );
      }
    }
    setSubmitting(true);
    try {
      const bookingRes = await clientApi.post(`/bookings`, {
        ...form,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
        language: lang,
      });
      const booking = bookingRes.data;
      const checkoutRes = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || t("common.error"));
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-booking" className="bg-white">
      <section className="container-flow section-pad">
        <button
          onClick={() => navigate(-1)}
          data-testid="booking-back"
          className="inline-flex items-center gap-2 font-heading uppercase tracking-widest text-xs font-bold text-forest mb-8 hover:text-burnt"
        >
          <ChevronLeft size={16} /> {lang === "fr" ? "Retour" : "Back"}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-14" data-testid="booking-form">
            <header>
              <p className="text-overline">{t("booking.overline")}</p>
              <h1 className="mt-4 hd-italic text-5xl md:text-6xl text-forest leading-tight">{t("booking.title")}</h1>
              <p className="mt-4 text-muted-foreground max-w-xl">{t("booking.subtitle")}</p>
            </header>

            {/* PACKAGE */}
            <div>
              <SectionTitle>{t("booking.step_package")}</SectionTitle>
              <FieldLabel required>{t("booking.step_package")}</FieldLabel>
              <Select value={form.package_id} onValueChange={set("package_id")}>
                <SelectTrigger data-testid="booking-package-select" className="w-full rounded-none border-2 border-border focus:border-burnt h-14 text-base">
                  <SelectValue placeholder={t("booking.select_package")} />
                </SelectTrigger>
                <SelectContent>
                  {packages.filter((p) => p.discipline !== "supplement").map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`booking-package-option-${p.id}`}>
                      {(lang === "fr" ? p.name_fr : p.name_en)} — {p.amount}$ CAD
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ABOUT YOU */}
            <div>
              <SectionTitle>{t("booking.step_about")}</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel required>{t("booking.full_name")}</FieldLabel>
                  <input
                    data-testid="booking-fullname"
                    className="input-line"
                    value={form.full_name}
                    onChange={setVal("full_name")}
                    required
                  />
                </div>
                <div>
                  <FieldLabel>{t("booking.gender")}</FieldLabel>
                  <Select value={form.gender} onValueChange={set("gender")}>
                    <SelectTrigger data-testid="booking-gender-select" className="rounded-none border-0 border-b-2 border-border focus:border-burnt h-12 px-0 text-base">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">{t("booking.gender_female")}</SelectItem>
                      <SelectItem value="male">{t("booking.gender_male")}</SelectItem>
                      <SelectItem value="other">{t("booking.gender_other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel>{t("booking.age")}</FieldLabel>
                  <input
                    data-testid="booking-age"
                    type="number"
                    min="0"
                    max="120"
                    className="input-line"
                    value={form.age}
                    onChange={setVal("age")}
                  />
                </div>
                <div>
                  <FieldLabel>{t("booking.region")}</FieldLabel>
                  <Select value={form.region} onValueChange={set("region")}>
                    <SelectTrigger data-testid="booking-region-select" className="rounded-none border-0 border-b-2 border-border focus:border-burnt h-12 px-0 text-base">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="montreal">{t("booking.region_montreal")}</SelectItem>
                      <SelectItem value="rive_sud">{t("booking.region_rive_sud")}</SelectItem>
                      <SelectItem value="rive_nord">{t("booking.region_rive_nord")}</SelectItem>
                      <SelectItem value="other">{t("booking.region_other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* SPORT PROFILE */}
            <div>
              <SectionTitle>{t("booking.step_profile")}</SectionTitle>

              <div className="mb-8">
                <FieldLabel>{t("booking.sport")}</FieldLabel>
                <RadioGroup value={form.sport_interest} onValueChange={set("sport_interest")} className="flex flex-wrap gap-6 mt-3">
                  {[
                    { v: "swim", l: t("booking.sport_swim") },
                    { v: "cycle", l: t("booking.sport_cycle") },
                    { v: "both", l: t("booking.sport_both") },
                  ].map((o) => (
                    <div key={o.v} className="flex items-center gap-2">
                      <RadioGroupItem value={o.v} id={`sport-${o.v}`} data-testid={`booking-sport-${o.v}`} className="border-forest text-burnt" />
                      <Label htmlFor={`sport-${o.v}`} className="text-base font-medium cursor-pointer">{o.l}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <FieldLabel required>{t("booking.level")}</FieldLabel>
                <RadioGroup value={form.level} onValueChange={set("level")} className="grid grid-cols-1 gap-3 mt-3">
                  {[
                    { v: "beginner", l: t("booking.level_beginner") },
                    { v: "intermediate", l: t("booking.level_intermediate") },
                    { v: "advanced", l: t("booking.level_advanced") },
                    { v: "competition", l: t("booking.level_competition") },
                    { v: "unknown", l: t("booking.level_unknown") },
                    { v: "rehab", l: t("booking.level_rehab") },
                  ].map((o) => (
                    <label
                      key={o.v}
                      htmlFor={`level-${o.v}`}
                      className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${
                        form.level === o.v ? "border-burnt bg-bone" : "border-border hover:border-forest"
                      }`}
                    >
                      <RadioGroupItem value={o.v} id={`level-${o.v}`} data-testid={`booking-level-${o.v}`} className="border-forest text-burnt" />
                      <span className="text-base">{o.l}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="mt-8">
                <FieldLabel>{t("booking.goals")}</FieldLabel>
                <textarea
                  data-testid="booking-goals"
                  className="input-line min-h-[100px] resize-none"
                  value={form.goals}
                  onChange={setVal("goals")}
                  placeholder={lang === "fr" ? "Ex. nager 1km, finir un 70.3, perdre du poids…" : "e.g. swim 1km, finish a 70.3, lose weight…"}
                />
              </div>
            </div>

            {/* CONTACT */}
            <div>
              <SectionTitle>{t("booking.step_contact")}</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel required>{t("booking.email")}</FieldLabel>
                  <input
                    data-testid="booking-email"
                    type="email"
                    className="input-line"
                    value={form.email}
                    onChange={setVal("email")}
                    required
                  />
                </div>
                <div>
                  <FieldLabel required>{t("booking.phone")}</FieldLabel>
                  <input
                    data-testid="booking-phone"
                    type="tel"
                    className="input-line"
                    value={form.phone}
                    onChange={setVal("phone")}
                    required
                  />
                </div>
              </div>
            </div>

            {/* DATE */}
            <div>
              <SectionTitle>{t("booking.step_date")}</SectionTitle>
              <div>
                <FieldLabel required>{t("booking.date")}</FieldLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date-trigger"
                      className="w-full input-line flex items-center justify-between text-left"
                    >
                      <span className={form.preferred_date ? "text-foreground" : "text-muted-foreground"}>
                        {form.preferred_date
                          ? format(form.preferred_date, "PPP", { locale: lang === "fr" ? frLocale : enUS })
                          : "—"}
                      </span>
                      <CalendarIcon size={18} className="text-burnt" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-none" align="start">
                    <Calendar
                      mode="single"
                      selected={form.preferred_date}
                      onSelect={set("preferred_date")}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      locale={lang === "fr" ? frLocale : enUS}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="mt-6">
                <FieldLabel>{t("booking.notes")}</FieldLabel>
                <textarea
                  data-testid="booking-notes"
                  className="input-line min-h-[80px] resize-none"
                  value={form.notes}
                  onChange={setVal("notes")}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="btn-primary w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t("booking.submitting") : t("booking.submit")}
            </button>
          </form>

          {/* SUMMARY */}
          <aside className="lg:col-span-5">
            <div className="bg-bone p-10 sticky top-28 border border-border">
              <p className="text-overline">{t("booking.summary_title")}</p>
              <div className="mt-6 space-y-5">
                {programTitle && (
                  <div data-testid="booking-summary-program" className="pb-5 border-b border-border">
                    <p className="text-sm text-muted-foreground uppercase tracking-widest">{t("booking.program")}</p>
                    <p className="hd-italic text-2xl text-forest mt-1">{programTitle}</p>
                  </div>
                )}
                {selectedPkg ? (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground uppercase tracking-widest">{t("booking.step_package")}</p>
                      <p className="hd-italic text-2xl text-forest mt-1">{lang === "fr" ? selectedPkg.name_fr : selectedPkg.name_en}</p>
                    </div>
                    <div className="border-t border-border pt-5 flex items-baseline justify-between">
                      <span className="text-sm uppercase tracking-widest text-muted-foreground">{t("booking.summary_title")}</span>
                      <span className="font-heading font-black text-4xl text-burnt">{selectedPkg.amount}$ CAD</span>
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground">{t("booking.select_package")}</p>
                )}
                {form.preferred_date && (
                  <div className="text-sm text-forest border-t border-border pt-5">
                    <p>{format(form.preferred_date, "PPP", { locale: lang === "fr" ? frLocale : enUS })}</p>
                  </div>
                )}
                {form.region && form.region !== "montreal" && (
                  <div className="text-xs text-burnt border-t border-border pt-5">
                    {lang === "fr"
                      ? "ℹ️ Séance à l'extérieur de Montréal : frais de déplacement de 15$ à 30$ ajoutés sur place (selon la distance)."
                      : "ℹ️ Session outside Montréal: $15 to $30 travel fee added on-site (depending on distance)."}
                  </div>
                )}
              </div>

              <div className="mt-10 pt-10 border-t border-border">
                <p className="font-heading uppercase tracking-widest text-xs font-bold text-burnt mb-2">{t("booking.need_help")}</p>
                <p className="text-sm text-muted-foreground">{t("booking.need_help_body")}</p>
                <p className="mt-3 text-sm text-forest">contact@flow-natation-cyclisme.com</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
